import { useEffect, useState, useRef, useCallback } from "react";
import useAuthStore from "../store/useAuthStore";
import Modal from "../component/Modal";
import { getTemplate } from "../services/TemplateServices";
import { sendEmail } from "../services/UserServices";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { deleteCard, getCards } from "../services/CardService";
import { ReactComponent as DeleteIcon } from "../assets/delete.svg";
import { ReactComponent as DownloadIcon } from "../assets/download.svg";
import InfiniteScroll from "react-infinite-scroll-component";

const CardsPage = () => {
  const { role, token } = useAuthStore();
  const clearToken = useAuthStore((state) => state.clearToken);

  const [cards, setCards] = useState([]);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedCard, setSelectedCard] = useState(null);
  const modalRef = useRef(null);

  const [templateModal, setTemplateModal] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  const [userImageModal, setUserImageModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  // Fetch cards with useCallback to prevent recreating on every render
  const fetchCards = useCallback(async (currentOffset, currentSearch, isNewSearch = false) => {
    try {
      const response = await getCards(limit, currentOffset, currentSearch);

      if (response.status !== 200) throw new Error("Fetch error");

      const { cards: newCards, count } = response.data;

      // If new search, replace cards. Otherwise append.
      setCards((prev) => isNewSearch ? newCards : [...prev, ...newCards]);
      setOffset(currentOffset + limit);
      
      // Calculate hasMore based on the new total
      const newTotal = currentOffset + newCards.length;
      setHasMore(newTotal < count);
    } catch (error) {
      console.error("Error fetching cards:", error);
      setHasMore(false);
    }
  }, [limit]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch cards when debounced search changes
  useEffect(() => {
    setCards([]);
    setOffset(0);
    setHasMore(true);
    fetchCards(0, debouncedSearch, true); // true = new search, replace data
  }, [debouncedSearch, fetchCards]);

  // Load more for infinite scroll
  const loadMore = useCallback(() => {
    if (hasMore) {
      fetchCards(offset, debouncedSearch, false); // false = append data
    }
  }, [offset, debouncedSearch, hasMore, fetchCards]);

  // Modal close handler
  const handleClickOutsideModal = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setSelectedCard(null);
    }
  }, []);

  useEffect(() => {
    if (selectedCard) {
      document.addEventListener("mousedown", handleClickOutsideModal);
      return () => document.removeEventListener("mousedown", handleClickOutsideModal);
    }
  }, [selectedCard, handleClickOutsideModal]);

  const sendEmailBtn = (email) => {
    setTemplateModal(true);
    setSelectedUser(email);
  };

  const allTemplates = useCallback(async () => {
    try {
      const res = await getTemplate();
      setTemplateList(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  }, []);

  useEffect(() => {
    allTemplates();
  }, [allTemplates]);

  const handleSendEmail = async () => {
    try {
      const response = await sendEmail(selectedTemplateId, selectedUser);
      toast.success(response?.data?.message || "Email sent");
      setTemplateModal(false);
      setSelectedTemplateId("");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleLogout = () => clearToken();

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this card?")) return;

    try {
      const res = await deleteCard(id);
      if (res.status === 200) {
        toast.success(res?.data?.message);
        setCards((prevCards) => prevCards.filter(card => String(card.id) !== String(id)));
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  function generateVCard(card) {
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${card.lastName};${card.firstName};;;`,
      `FN:${card.firstName} ${card.lastName}`,
      card.company ? `ORG:${card.company}` : null,
      card.position ? `TITLE:${card.position}` : null,
      card.mobile ? `TEL;TYPE=CELL:${card.mobile}` : null,
      card.email ? `EMAIL:${card.email}` : null,
      card.website ? `URL:${card.website}` : null,
      "END:VCARD"
    ].filter(Boolean);

    return lines.join("\r\n");
  }

  const downloadCard = (card) => {
    const vcard = generateVCard(card);
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `${card.firstName}_${card.lastName}.vcf`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-bold mb-4">Visiting Cards</h2>
        <button
          className="bg-blue-700 text-white rounded-md px-4 h-8"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      <div className="flex justify-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search name, email, company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border px-3 py-1 rounded-md w-64"
        />
      </div>

      {/* Infinite Scroll */}
      <div id="scrollableDiv" style={{ height: "80vh", overflowY: "auto" }}>
        <InfiniteScroll
          dataLength={cards.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<h4 className="text-center py-4">Loading...</h4>}
          endMessage={<p className="text-center py-4 font-semibold">— No more cards —</p>}
          scrollableTarget="scrollableDiv"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div
                key={card.id}
                className="bg-white shadow-md p-4 rounded-lg hover:shadow-lg transition cursor-pointer flex flex-col"
                onClick={() => setSelectedCard(card)}
              >
                <div>
                  <div className="flex justify-between">
                    <h3 className="text-lg font-semibold">
                      {card.firstName} {card.lastName}
                    </h3>

                    {role === "admin" && (
                      <div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCard(card);
                          }}
                        >
                          <DownloadIcon className="h-5 w-7" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(card.id);
                          }}
                        >
                          <DeleteIcon className="h-5 w-7" />
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600">{card.position}</p>
                  <p className="text-gray-500">{card.company}</p>
                  <p className="text-blue-500">{card.email}</p>
                  <p className="text-gray-500">{card.mobile || "-"}</p>

                  {card.website && (
                    <a
                      href={card.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {card.website}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      </div>

      {/* Card Modal */}
      {selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            ref={modalRef}
            className="bg-white p-6 rounded-lg shadow-lg max-w-lg relative"
          >
            <button
              onClick={() => setSelectedCard(null)}
              className="absolute top-2 right-2 text-2xl font-bold hover:text-gray-700"
            >
              &times;
            </button>

            <img
              src={selectedCard.imageUrl}
              alt={`${selectedCard.firstName} ${selectedCard.lastName}`}
              className="w-full max-h-80 object-cover rounded-md mb-3"
            />

            <h3 className="text-lg font-semibold">
              {selectedCard.firstName} {selectedCard.lastName}
            </h3>
            <p>{selectedCard.position}</p>
            <p>{selectedCard.company}</p>
            <p>{selectedCard.email}</p>
            <p>{selectedCard.mobile}</p>

            {selectedCard.remark && (
              <p className="mt-2">
                <b>Remark:</b> {selectedCard.remark}
              </p>
            )}

            <p className="mt-2">
              <b>Back Image:</b>{" "}
              {selectedCard.backImageUrl ? (
                <a
                  href={selectedCard.backImageUrl}
                  target="_blank"
                  className="text-blue-600 underline"
                  rel="noreferrer"
                >
                  View
                </a>
              ) : (
                "No back image"
              )}
            </p>

            {selectedCard.userImage && (
              <p
                className="text-blue-700 underline cursor-pointer mt-2"
                onClick={() => {
                  setUserImageModal(true);
                  setPreviewImage(selectedCard.userImage);
                }}
              >
                Preview user image
              </p>
            )}

            {role === "admin" && (
              <div className="mt-4 text-center">
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                  onClick={() => sendEmailBtn(selectedCard.email)}
                >
                  Send Email
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      <Modal isOpen={templateModal} onClose={() => setTemplateModal(false)}>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Select Template</h2>

          <select
            className="w-full border p-2 rounded-md"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">Choose a template</option>
            {templateList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <div className="mt-6 flex justify-end gap-3">
            <button
              className="px-4 py-2 border rounded-md hover:bg-gray-100"
              onClick={() => setTemplateModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!selectedTemplateId}
              onClick={handleSendEmail}
            >
              Send
            </button>
          </div>
        </div>
      </Modal>

      {/* User Image Modal */}
      <Modal
        isOpen={userImageModal}
        onClose={() => {
          setUserImageModal(false);
          setPreviewImage(null);
        }}
      >
        <div className="flex justify-center items-center h-72">
          <img
            src={previewImage}
            alt="user"
            className="max-h-full max-w-full object-contain rounded-md"
          />
        </div>
      </Modal>
    </div>
  );
};

export default CardsPage;